// @mui
import { Box, Divider, Grid, Skeleton, Stack, useTheme } from '@mui/material';

// ---------------------------------------------------------------------

type OrganizationUpcomingVisitCardSkeletonProps = {
  actions?: boolean;
  isSmall?: boolean;
  isPatientContext?: boolean;
};

export default function OrganizationUpcomingVisitCardSkeleton({
  isSmall: _isSmall,
  actions = true,
  isPatientContext = false,
}: OrganizationUpcomingVisitCardSkeletonProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: theme.palette.background.default,
        boxShadow: theme.customShadows.z8,
        marginTop: { xs: 1, sm: 1, md: 1, lg: 0, xl: 0 },
      }}
    >
      <Grid container>
        {/* Header */}
        <Grid item xs={12} p={2} pb={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="rounded" height={28} width="60%" />
          </Stack>
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Grid>

        {/* Content */}
        {isPatientContext ? (
          <>
            {/* Tab headers */}
            <Grid item xs={12} px={2}>
              <Stack
                direction="row"
                spacing={3}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Skeleton variant="rounded" height={40} width={110} />
                <Skeleton variant="rounded" height={40} width={150} />
              </Stack>
            </Grid>
            {/* Tab content */}
            <Grid item xs={12} p={2} py={1.5}>
              <Stack sx={{ gap: 1 }}>
                <Skeleton variant="rounded" height={18} width="100%" />
                <Skeleton variant="rounded" height={18} width="90%" />
                <Skeleton variant="rounded" height={18} width="80%" />
              </Stack>
            </Grid>
          </>
        ) : (
          <Grid item xs={12} p={2} py={1.5}>
            <Stack sx={{ gap: 2 }}>
              <Stack sx={{ gap: 1 }}>
                <Skeleton variant="rounded" height={20} width={140} />
                <Skeleton variant="rounded" height={18} width="100%" />
                <Skeleton variant="rounded" height={18} width="90%" />
                <Skeleton variant="rounded" height={18} width="85%" />
              </Stack>

              <Stack sx={{ gap: 1 }}>
                <Skeleton variant="rounded" height={20} width={160} />
                <Skeleton variant="rounded" height={18} width="80%" />
                <Skeleton variant="rounded" height={18} width="70%" />
              </Stack>

              {/* Attachments list placeholder */}
              <Stack sx={{ gap: 1, overflow: 'hidden' }}>
                <Skeleton variant="rounded" height={20} width={120} />
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {/* File Chip 1 */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Skeleton variant="rounded" width={28} height={28} />
                    <Skeleton variant="rounded" width={220} height={18} />
                  </Box>

                  {/* File Chip 2 */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Skeleton variant="rounded" width={28} height={28} />
                    <Skeleton variant="rounded" width={180} height={18} />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Skeleton variant="rounded" width={28} height={28} />
                    <Skeleton variant="rounded" width={180} height={18} />
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        )}

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Grid>

        {/* Actions */}
        {actions && (
          <Grid item xs={12} p={2} pt={1.5}>
            <Stack
              sx={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 2 }}
            >
              <Skeleton variant="rounded" height={36} width={160} />
              <Skeleton variant="rounded" height={36} width={140} />
            </Stack>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
